import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { SHIFT_DAY_LABELS, shiftDayOf, type CarePassOutcome } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { optionalDay, optionalOneOf, optionalString, optionalTrue, KEEP_PLACE } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { ActionFilterChips } from "./-components/action-filter";
import {
  type BoardItem,
  type BoardResident,
  itemsUnder,
  openItems,
} from "./-components/care-pass-model";
import { ACTION_FILTERS, type ActionFilter } from "./-components/action-filters";
import { MarkDialog, type MarkTarget } from "./-components/mark-dialog";
import { PassCard } from "./-components/pass-card";
import { PassHeaderActions } from "./-components/pass-header-actions";
import { PassSummary } from "./-components/pass-summary";
import { SelectionBar } from "./-components/selection-bar";
import { ShiftStrip } from "./-components/shift-strip";
import { usePassRecording } from "./-components/use-pass-recording";

type PassSearch = {
  facility?: string;
  date?: string;
  shift?: string;
  filter?: ActionFilter;
  everyone?: true;
};

export const Route = createFileRoute("/_authenticated/_care/care-pass/")({
  validateSearch: (search: Record<string, unknown>): PassSearch => ({
    facility: optionalString(search.facility)?.slice(0, 60) || undefined,
    date: optionalDay(search.date),
    shift: optionalString(search.shift)?.slice(0, 60) || undefined,
    filter: optionalOneOf(ACTION_FILTERS, search.filter),
    everyone: optionalTrue(search.everyone),
  }),
  component: CarePassPage,
});

const GRID = "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";

/** The care pass: one shift of one day, every resident with the care owed them on it. The open
 * shift comes up on its own; the strip flips to another, the picker to another day. A tap marks
 * one thing done; a selection marks many at once. */
function CarePassPage() {
  const queryClient = useQueryClient();
  const { facilityId: globalFacility } = useFacility();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<PassSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const facilityOverride = params.facility ?? null;
  const date = params.date ?? "";
  const [otherBuilding, setOtherBuilding] = useState(false);
  const shiftId = otherBuilding ? null : (params.shift ?? null);
  const filter: ActionFilter = params.filter ?? "all";
  const setFilter = (next: ActionFilter) => patch({ filter: next === "all" ? undefined : next });
  const showEveryone = Boolean(params.everyone);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [marking, setMarking] = useState<MarkTarget | null>(null);

  const facilities = useQuery(
    orpc.administration.facilities.list.queryOptions({ input: { includeInactive: false } }),
  );
  const facilityId =
    globalFacility !== "all"
      ? globalFacility
      : (facilityOverride ?? facilities.data?.[0]?.id ?? "");
  const [shownFacility, setShownFacility] = useState(facilityId);
  if (shownFacility !== facilityId) {
    setShownFacility(facilityId);
    if (shownFacility) setOtherBuilding(true);
    setSelected(new Set());
  }
  const canRecord = useCan("carepass:record", facilityId || null);
  const canManage = useCan("carepass:manage", facilityId || null);

  const board = useQuery({
    ...orpc.care.carePass.board.queryOptions({
      input: {
        facilityId,
        ...(date ? { date } : {}),
        ...(shiftId ? { shiftId } : {}),
      },
    }),
    enabled: Boolean(facilityId),
  });
  const data = board.data;
  const passDate = data?.date ?? date;
  const recording = usePassRecording({
    facilityId,
    date: passDate,
    shiftId: data?.shift?.id ?? null,
  });

  const everyone = data?.residents ?? [];
  const people = everyone.filter((person) => showEveryone || itemsUnder(person, filter).length > 0);
  const counts = Object.fromEntries(
    ACTION_FILTERS.map((key) => [
      key,
      everyone.reduce((sum, person) => sum + openItems(person, key).length, 0),
    ]),
  ) as Record<ActionFilter, number>;
  const chosen = people.filter((person) => selected.has(person.id));
  const chosenOpen = chosen.flatMap((person) =>
    openItems(person, filter).map((item) => ({ residentId: person.id, action: item.action })),
  );

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: orpc.care.carePass.board.key() });
  const pickDate = (next: string) => {
    setOtherBuilding(false);
    patch({ date: next || undefined, shift: undefined });
    setSelected(new Set());
  };
  const pickShift = (id: string) => {
    setOtherBuilding(false);
    patch({ shift: id, date: passDate || undefined });
    setSelected(new Set());
  };
  const markDone = (person: BoardResident, item: BoardItem) =>
    recording.mark([{ residentId: person.id, action: item.action }]);
  const markAll = (person: BoardResident) =>
    recording.mark(
      openItems(person, filter).map((item) => ({ residentId: person.id, action: item.action })),
    );
  const markSelection = () => {
    recording.mark(chosenOpen);
    setSelected(new Set());
  };
  const recordFromDialog = (outcome: CarePassOutcome, note: string) => {
    if (!marking) return;
    recording.mark(marking.entries, outcome, note);
    setMarking(null);
    setSelected(new Set());
  };

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-5">
      <PageHeader
        title="Care pass"
        meta={data ? <PassSummary board={data} /> : null}
        actions={
          <PassHeaderActions
            facilities={globalFacility === "all" ? (facilities.data ?? []) : null}
            facilityId={facilityId}
            onFacility={(next) => patch({ facility: next || undefined })}
            date={passDate}
            onDate={pickDate}
            refreshing={board.isFetching}
            onRefresh={refresh}
            canManage={canManage}
          />
        }
      />

      {board.isPending && facilityId ? (
        <div className="flex gap-2 overflow-hidden">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-24 min-w-[9.5rem] flex-1 rounded-lg" />
          ))}
        </div>
      ) : board.isError ? (
        <ReadFailed
          message="Couldn't load the care pass."
          error={board.error}
          retrying={board.isFetching}
          onRetry={() => void board.refetch()}
        />
      ) : data && data.shifts.length > 0 ? (
        <ShiftStrip shifts={data.shifts} selected={data.shift?.id ?? null} onSelect={pickShift} />
      ) : null}

      {!facilityId && facilities.isSuccess ? (
        <EmptyState
          title="No facility available"
          description="Ask an administrator to assign you to a facility."
        />
      ) : data && !data.shift ? (
        <EmptyState
          title={`No shift runs on ${SHIFT_DAY_LABELS[shiftDayOf(passDate)]}`}
          description="The building's care shifts leave this day out, so nothing is owed on it."
          action={
            canManage ? (
              <Button variant="outline" asChild>
                <Link to="/care-pass/shifts">Set up the shifts</Link>
              </Button>
            ) : undefined
          }
        />
      ) : data ? (
        <section aria-labelledby="pass-residents" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 id="pass-residents" className="text-sm font-medium">
              Residents
              <span className="text-muted-foreground ml-1.5 font-normal tabular-nums">
                {people.length}
              </span>
            </h2>
            <ActionFilterChips value={filter} counts={counts} onChange={setFilter} />
          </div>
          {canRecord ? (
            <SelectionBar
              shown={people.length}
              selected={chosen.length}
              filter={filter}
              openCount={chosenOpen.length}
              busy={recording.busy}
              onSelectAll={() => setSelected(new Set(people.map((person) => person.id)))}
              onClear={() => setSelected(new Set())}
              onMarkDone={markSelection}
              onMarkOther={() => setMarking({ residents: chosen, entries: chosenOpen })}
            />
          ) : null}
          {people.length === 0 ? (
            <EmptyState
              title={
                everyone.length === 0
                  ? "No residents in care at this facility"
                  : "Nothing of this kind is owed on this shift"
              }
              description={
                everyone.length === 0
                  ? "Residents appear here once they are admitted to this building."
                  : "Residents with nothing scheduled are hidden. Show everyone to record care that was not scheduled."
              }
              action={
                everyone.length > 0 && !showEveryone ? (
                  <Button variant="outline" onClick={() => patch({ everyone: true })}>
                    Show all residents
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className={GRID}>
              {people.map((person) => (
                <PassCard
                  key={person.id}
                  resident={person}
                  filter={filter}
                  selected={selected.has(person.id)}
                  canRecord={canRecord}
                  busy={recording.busy}
                  onSelect={(checked) =>
                    setSelected((current) => {
                      const next = new Set(current);
                      if (checked) next.add(person.id);
                      else next.delete(person.id);
                      return next;
                    })
                  }
                  onDone={(item) => markDone(person, item)}
                  onAllDone={() => markAll(person)}
                  onOpenMark={(item) =>
                    setMarking({
                      residents: [person],
                      entries: [{ residentId: person.id, action: item.action }],
                      existing: item.log,
                      away: item.away,
                    })
                  }
                />
              ))}
            </div>
          )}
        </section>
      ) : null}

      {marking ? (
        <MarkDialog
          target={marking}
          busy={recording.busy}
          canRemove={canRecord}
          onRecord={recordFromDialog}
          onRemove={(logId) => {
            recording.remove(logId);
            setMarking(null);
          }}
          onClose={() => setMarking(null)}
        />
      ) : null}
    </div>
  );
}
